"use client";

import { getBlob, ref } from "firebase/storage";
import { AnimatePresence, motion } from "framer-motion";
import { Eye, ImageIcon, Loader2, Sparkles, X, Zap, Users } from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Effect, Style } from "../../types/effects";
import { selectUserGlobalData } from "../../store/dreamworldSlice";
import type { LibraryItem } from "../../types/library";
import eventBus from "../../utils/eventBus";
import { storage } from "../../utils/firebase";
import { decreaseFluxBalance, hasEnoughFlux } from "../../utils/fluxManager";
import InsufficientFluxModal from "../modals/InsufficientFluxModal";
import UploadsPanel from "../uploads/UploadsPanel";
import dreamWorldProvider from "../../DreamWorldProvider";

interface EffectDescriptionPanelProps {
  effect: Effect | null;
  onClose: () => void;
  onStartEditing: (
    effect: Effect,
    images: File[],
    userInput?: string,
    style?: Style
  ) => void;
  preventExitFullscreen?: boolean;
}

const EffectDescriptionPanel: React.FC<EffectDescriptionPanelProps> = ({
  effect,
  onClose,
  onStartEditing,
  preventExitFullscreen,
}) => {
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [selectedImagePreviews, setSelectedImagePreviews] = useState<string[]>(
    []
  );
  const [userInput, setUserInput] = useState("");
  const [selectedStyle, setSelectedStyle] = useState<Style | undefined>(
    undefined
  );
  const [isUploadsPanelOpen, setIsUploadsPanelOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const user = useSelector(selectUserGlobalData);
  const navigate = useNavigate();
  const [hasSufficientFlux, setHasSufficientFlux] = useState(true);
  const [isFluxModalOpen, setIsFluxModalOpen] = useState(false);

  useEffect(() => {
    if (effect) {
      eventBus.emit("fullscreen:enter");
      if (user && user.userData && effect.fluxCost && effect.fluxCost > 0) {
        hasEnoughFlux(user.userData.uid, effect.fluxCost).then(
          setHasSufficientFlux
        );
      } else {
        setHasSufficientFlux(true);
      }
    } else {
      // Reset state when effect is null (panel closes)
      setSelectedImages([]);
      setSelectedImagePreviews([]);
      setUserInput("");
      setIsLoading(false);
    }
    return () => {
      if (!preventExitFullscreen) {
        eventBus.emit("fullscreen:exit");
      }
    };
  }, [effect, user, preventExitFullscreen]);

  const handleOnClose = () => {
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const requiredCount = effect?.photosRequiredCount || 1;
      const newImages = [...selectedImages, ...files].slice(0, requiredCount);
      setSelectedImages(newImages);

      const newPreviews: string[] = [];
      newImages.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews.push(reader.result as string);
          if (newPreviews.length === newImages.length) {
            setSelectedImagePreviews(newPreviews);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleImageSelect = async (image: LibraryItem) => {
    try {
      setIsUploadsPanelOpen(false);
      const requiredCount = effect?.photosRequiredCount || 1;
      if (selectedImages.length >= requiredCount) return;

      const match = decodeURIComponent(image.fullUrl).match(/o\/(.*?)\?/);
      if (!match) throw new Error("Invalid Firebase URL");
      const objectPath = match[1];
      const imageRef = ref(storage, objectPath);
      const blob = await getBlob(imageRef);
      const file = new File([blob], image.title || "image.png", {
        type: blob.type,
      });

      setSelectedImages((prev) => [...prev, file]);
      setSelectedImagePreviews((prev) => [...prev, image.fullUrl]);
    } catch (error) {
      console.error("Error fetching image:", error);
    }
  };

  const handleStartEditing = async () => {
    if (effect && selectedImages.length > 0 && user && user.userData) {
      if (!hasSufficientFlux) {
        setIsFluxModalOpen(true);
        return;
      }

      setIsLoading(true);

      // This logic should be handled by a backend transaction for reliability
      try {
        if (effect.fluxCost && effect.fluxCost > 0) {
          await decreaseFluxBalance(user.userData.uid, effect.fluxCost);
        }

        // Increment use count for the effect
        await dreamWorldProvider.incrementEffectUseCount(
          effect.id,
          user.userData.uid
        );

        onStartEditing(effect, selectedImages, userInput, selectedStyle);
      } catch (error) {
        console.error("Editing failed:", error);
        setIsLoading(false);
        // Optionally, show a more specific error to the user
        setHasSufficientFlux(false);
        setIsFluxModalOpen(true);
      }
    }
  };

  const photosRequiredCount = effect?.photosRequiredCount || 1;
  const isReadyToStart =
    selectedImages.length === photosRequiredCount && !isLoading;

  return (
    <AnimatePresence>
      {effect && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={handleOnClose}
          />

          <motion.div
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 30, stiffness: 250 }}
            className="fixed inset-4 md:inset-x-8 md:inset-y-4 lg:left-1/2 lg:right-auto lg:top-4 lg:bottom-4 lg:transform lg:-translate-x-1/2 lg:w-[650px] lg:max-w-2xl bg-white/10 backdrop-blur-2xl border border-white/20 rounded-2xl shadow-2xl z-50 max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-2rem)] overflow-y-auto"
          >
            <div className="flex flex-col p-6 min-h-full">
              {/* Enhanced Header */}
              <div className="flex items-start justify-between mb-6 pb-6 border-b border-white/10">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-4 mb-3">
                    <h2 className="text-2xl font-bold text-white truncate">
                      {effect.title}
                    </h2>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="flex items-center gap-1 px-2 py-1 bg-white/5 rounded-full">
                        <Users size={14} className="text-blue-300" />
                        <span className="text-white/80">{effect.useCount}</span>
                      </div>
                      {effect.userUsage && (
                        <div className="flex items-center gap-1 px-2 py-1 bg-purple-500/20 rounded-full">
                          <Sparkles size={14} className="text-purple-300" />
                          <span className="text-purple-200">
                            {effect.userUsage.useCount}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed line-clamp-2">
                    {effect.description}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => navigate(`/effects/${effect.id}`)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors group"
                    title="View Effect Page"
                  >
                    <Eye
                      size={18}
                      className="text-white/60 group-hover:text-white"
                    />
                  </button>
                  <button
                    onClick={handleOnClose}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors group"
                  >
                    <X
                      size={20}
                      className="text-white/60 group-hover:text-white"
                    />
                  </button>
                </div>
              </div>

              {/* Optimal Images Description */}
              {effect.optimalImagesDescription && (
                <div className="mb-4 p-3 bg-blue-500/10 rounded-lg border border-blue-400/20">
                  <h4 className="text-blue-200 text-sm font-medium mb-1">
                    Optimal Images
                  </h4>
                  <p className="text-white/70 text-xs leading-relaxed">
                    {effect.optimalImagesDescription}
                  </p>
                </div>
              )}

              {/* User Input */}
              {effect.userInputPlaceholder && (
                <div className="mb-4">
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    Additional Input
                  </label>
                  <input
                    type="text"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    placeholder={effect.userInputPlaceholder}
                    className="w-full p-2.5 bg-white/5 border border-white/20 rounded-lg text-white text-sm placeholder-white/40 focus:ring-2 focus:ring-purple-400 focus:border-purple-400 outline-none transition-all"
                  />
                </div>
              )}

              {/* Styles Section - Horizontal Scroll */}
              {effect.styles && (
                <div className="mb-5">
                  <h3 className="text-white/90 text-sm font-medium mb-3">
                    Select Style
                  </h3>
                  <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2">
                    {effect.styles.map((style) => (
                      <div
                        key={style.name}
                        className={`flex-shrink-0 w-20 cursor-pointer group`}
                        onClick={() => setSelectedStyle(style)}
                      >
                        <div
                          className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                            selectedStyle?.name === style.name
                              ? "border-purple-400 shadow-lg shadow-purple-400/25"
                              : "border-white/20 group-hover:border-white/40"
                          }`}
                        >
                          <img
                            src={style.image}
                            alt={style.name}
                            className="w-full h-full object-cover"
                          />
                          {selectedStyle?.name === style.name && (
                            <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center">
                              <div className="w-4 h-4 bg-purple-400 rounded-full border-2 border-white" />
                            </div>
                          )}
                        </div>
                        <p className="text-white/80 text-xs text-center mt-1 truncate px-1">
                          {style.name}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Image Selection */}
              <div className="mb-6">
                <h3 className="text-white/90 text-sm font-medium mb-3">
                  Select {photosRequiredCount}{" "}
                  {photosRequiredCount > 1 ? "Images" : "Image"}
                </h3>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  ref={fileInputRef}
                  multiple={photosRequiredCount > 1}
                />
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                  {selectedImagePreviews.map((preview, index) => (
                    <div key={index} className="relative aspect-square">
                      <img
                        src={preview}
                        alt={`Selected ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg shadow-md"
                      />
                      <div className="absolute top-1 right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                        <span className="text-white text-xs font-bold">
                          {index + 1}
                        </span>
                      </div>
                    </div>
                  ))}
                  {selectedImages.length < photosRequiredCount && (
                    <motion.div
                      onClick={() => setIsUploadsPanelOpen(true)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="cursor-pointer p-3 bg-white/5 border-2 border-dashed border-white/30 rounded-lg hover:bg-white/8 hover:border-white/40 transition-all flex flex-col items-center justify-center aspect-square"
                    >
                      <ImageIcon size={24} className="text-white/60 mb-1" />
                      <p className="text-white/80 text-xs font-medium">Add</p>
                      <p className="text-white/50 text-xs">
                        {selectedImages.length + 1}/{photosRequiredCount}
                      </p>
                    </motion.div>
                  )}
                </div>
              </div>

              {/* Action Button */}
              <motion.button
                onClick={handleStartEditing}
                disabled={!isReadyToStart}
                className="w-full flex items-center justify-center gap-3 p-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all duration-300 font-medium relative overflow-hidden"
                whileHover={{ scale: isReadyToStart ? 1.01 : 1 }}
                whileTap={{ scale: isReadyToStart ? 0.99 : 1 }}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    <span>Start Creating</span>
                    {effect.fluxCost && effect.fluxCost > 0 && (
                      <div className="flex items-center gap-1 text-sm bg-black/20 px-2 py-1 rounded-full ml-2">
                        <Zap size={12} />
                        <span>{effect.fluxCost}</span>
                      </div>
                    )}
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>

          <InsufficientFluxModal
            isOpen={isFluxModalOpen}
            onClose={() => setIsFluxModalOpen(false)}
          />

          <UploadsPanel
            isOpen={isUploadsPanelOpen}
            onClose={() => setIsUploadsPanelOpen(false)}
            onImageSelect={handleImageSelect}
          />
        </>
      )}
    </AnimatePresence>
  );
};

export default EffectDescriptionPanel;
